import { TrendingUp, Percent, Users, Award, ShieldCheck, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { formatPilotageCurrency, formatPilotageDecimal, normalizePercentValue, computeObjectiveAttainment } from "@/lib/pilotageUi";
import AnnualMarginExplainer from "./AnnualMarginExplainer";

export default function MonthlyAnnualSynthesis({
  year,
  objective,
  cockpitData,
}) {
  const isFutureYear = year > new Date().getFullYear();
  const annualTargets = objective?.targets || {};
  const comparisons = cockpitData?.comparisons || {};
  const finance = cockpitData?.finance || {};
  const staffing = cockpitData?.staffing || {};
  const forecast = cockpitData?.forecast || {};

  // 1. Chiffre d'Affaires Annuel
  const targetCa = comparisons.ca_annual_net?.target ?? annualTargets.annual_revenue_target ?? null;
  const actualCa = comparisons.ca_annual_net?.actual ?? finance.ca_annual_net ?? null;
  const projectedCa = forecast.projected_ca_annual_net ?? null;
  const caCompletionRate = targetCa && actualCa ? Math.min(100, Math.round((actualCa / targetCa) * 100)) : null;

  // 2. Marge Annuelle
  const rawTargetMargin = comparisons.margin_pct?.target ?? annualTargets.margin_target ?? null;
  const targetMargin = normalizePercentValue(rawTargetMargin);

  const rawActualMargin = comparisons.margin_pct?.actual ?? finance.average_margin_pct ?? null;
  const actualMargin = normalizePercentValue(rawActualMargin);

  const marginDeltaPts = actualMargin != null && targetMargin != null ? actualMargin - targetMargin : null;

  const caRetenu = finance.ca_annual_net ?? actualCa;
  const margeMontant = finance.mission_profit_annual ?? (caRetenu != null && actualMargin != null ? (caRetenu * actualMargin) / 100 : null);
  const coutsRetenus = (caRetenu != null && margeMontant != null) ? caRetenu - margeMontant : (finance.expenses_annual_total ?? finance.mission_expenses_annual ?? null);
  const isMarginCalculationAvailable = caRetenu != null && caRetenu > 0 && actualMargin != null;

  // 3. ETP Annuel (Base 210j)
  const targetEtp = comparisons.annual_billed_etp?.target ?? annualTargets.total_annual_billed_etp ?? null;
  const actualEtp = comparisons.annual_billed_etp?.actual ?? staffing.annual_billed_etp ?? null;
  const etpDelta = actualEtp != null && targetEtp != null ? actualEtp - targetEtp : null;

  // 4. Effectif Total (Ressources fin d'année)
  const targetResources = comparisons.resources_end_year?.target ?? annualTargets.total_resources_end_year ?? null;
  const actualResources = comparisons.resources_end_year?.actual ?? staffing.resources_end_year ?? staffing.total_collaborators ?? null;
  const resourcesDelta = actualResources != null && targetResources != null ? actualResources - targetResources : null;

  // Calculs standardisés d'atteinte d'objectifs (Point 5C)
  const effectiveCaVal = actualCa != null ? actualCa : (isFutureYear ? projectedCa : null);
  const caAttainment = computeObjectiveAttainment(effectiveCaVal, targetCa, { isForecast: isFutureYear });
  const marginAttainment = computeObjectiveAttainment(actualMargin, targetMargin, { isForecast: isFutureYear });
  const etpAttainment = computeObjectiveAttainment(actualEtp, targetEtp, { isForecast: isFutureYear });
  const resourcesAttainment = computeObjectiveAttainment(actualResources, targetResources, { isForecast: isFutureYear });

  return (
    <div
      className="space-y-3 rounded-2xl border border-white/10 bg-slate-950/60 p-4 sm:p-5"
      data-testid="monthly-annual-synthesis"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Award className="h-4 w-4 text-lenvia-blue" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-white">
            Trajectoire Annuelle Macro — {year}
          </h3>
          <span className="rounded-full border border-lenvia-blue/30 bg-lenvia-blue/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-lenvia-blue">
            {objective?.name || "Scénario de référence"}
          </span>
        </div>

        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            <span>Réalisé YTD</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-lenvia-blue" />
            <span>Objectif Annuel</span>
          </span>
          {isFutureYear && (
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-purple-400" />
              <span>Projection Budgétaire</span>
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {/* CARTE 1: CA ANNUEL */}
        <div className="rounded-xl border border-white/5 bg-slate-900/80 p-3.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-400">Chiffre d'affaires annuel</span>
            <TrendingUp className="h-4 w-4 text-lenvia-blue" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-xl font-bold text-white">
              {actualCa != null ? formatPilotageCurrency(actualCa) : isFutureYear && projectedCa != null ? formatPilotageCurrency(projectedCa) : "—"}
            </span>
            {targetCa != null && (
              <span className="text-xs text-slate-400">
                / {formatPilotageCurrency(targetCa)}
              </span>
            )}
          </div>
          <div className="mt-2 space-y-1">
            <div className="flex flex-wrap items-center justify-between gap-1 text-[10px]">
              <span className="font-semibold text-slate-300">
                {caAttainment.attainmentText}
              </span>
              {caAttainment.varianceText ? (
                <span className={caAttainment.relativeVariance >= 0 ? "text-emerald-400 font-medium" : "text-rose-400 font-medium"}>
                  {caAttainment.varianceText}
                </span>
              ) : null}
            </div>
            {caAttainment.defined && (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
                <div
                  className="h-full rounded-full bg-lenvia-blue transition-all"
                  style={{ width: `${Math.min(100, Math.max(0, caAttainment.progress))}%` }}
                />
              </div>
            )}
          </div>
        </div>

        {/* CARTE 2: MARGE ANNUELLE */}
        <div
          className="flex flex-col justify-between rounded-xl border border-white/5 bg-slate-900/80 p-3.5"
          data-testid="card-annual-margin"
        >
          <div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-medium text-slate-400">Marge annuelle</span>
                <p className="text-[10px] text-slate-400">Rentabilité de l'activité sur l'exercice</p>
              </div>
              <div className="flex items-center gap-1.5">
                <span
                  className="rounded border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-slate-400"
                  data-testid="annual-margin-nature"
                >
                  {isFutureYear ? "Marge prévisionnelle" : "Marge réalisée à date"}
                </span>
                <Percent className="h-4 w-4 text-emerald-400" />
              </div>
            </div>

            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-xl font-bold text-white" data-testid="annual-margin-actual">
                {actualMargin != null ? `${formatPilotageDecimal(actualMargin)} %` : "—"}
              </span>
              {targetMargin != null && (
                <span className="text-xs text-slate-400" data-testid="annual-margin-target">
                  / {formatPilotageDecimal(targetMargin)} %
                </span>
              )}
            </div>

            <div className="mt-2 flex items-center gap-1.5 text-xs">
              {marginDeltaPts != null ? (
                <span
                  className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                    marginDeltaPts >= 0
                      ? "bg-emerald-500/10 text-emerald-400"
                      : "bg-rose-500/10 text-rose-400"
                  }`}
                  data-testid="annual-margin-delta-badge"
                >
                  {marginDeltaPts >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                  {marginDeltaPts >= 0 ? "+" : ""}{formatPilotageDecimal(marginDeltaPts)} pts
                  <span className="ml-1 text-[10px] font-normal opacity-80">
                    ({marginDeltaPts >= 0 ? "Favorable" : "Défavorable"})
                  </span>
                </span>
              ) : (
                <span className="text-[11px] text-slate-400">
                  {marginAttainment.defined ? "Cible annuelle" : "Objectif non défini"}
                </span>
              )}
              <span className="text-[11px] text-slate-400">vs objectif</span>
            </div>
            {marginAttainment.defined && (
              <div className="mt-1 flex flex-wrap items-center justify-between gap-1 text-[10px]">
                <span className="text-slate-300 font-medium">{marginAttainment.attainmentText}</span>
                {marginAttainment.varianceText ? (
                  <span className={marginAttainment.relativeVariance >= 0 ? "text-emerald-400 font-medium" : "text-rose-400 font-medium"}>
                    {marginAttainment.varianceText}
                  </span>
                ) : null}
              </div>
            )}
          </div>

          <div className="mt-3 border-t border-white/5 pt-2.5">
            <AnnualMarginExplainer
              year={year}
              isFutureYear={isFutureYear}
              scenarioName={objective?.name || "Scénario de référence"}
              caRetenu={caRetenu}
              coutsRetenus={coutsRetenus}
              margeMontant={margeMontant}
              actualMargin={actualMargin}
              targetMargin={targetMargin}
              marginDeltaPts={marginDeltaPts}
              isAvailable={isMarginCalculationAvailable}
            />
          </div>
        </div>

        {/* CARTE 3: ETP ANNUEL */}
        <div className="rounded-xl border border-white/5 bg-slate-900/80 p-3.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-400">ETP facturés (Base 210j)</span>
            <ShieldCheck className="h-4 w-4 text-purple-400" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-xl font-bold text-white">
              {actualEtp != null ? `${formatPilotageDecimal(actualEtp)} ETP` : "—"}
            </span>
            {targetEtp != null && (
              <span className="text-xs text-slate-400">
                / {formatPilotageDecimal(targetEtp)} ETP
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-xs">
            {etpDelta != null ? (
              <span
                className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                  etpDelta >= 0
                    ? "bg-emerald-500/10 text-emerald-400"
                    : "bg-rose-500/10 text-rose-400"
                }`}
              >
                {etpDelta >= 0 ? "+" : ""}{formatPilotageDecimal(etpDelta)} ETP
              </span>
            ) : (
              <span className="text-[11px] text-slate-400">
                {etpAttainment.defined ? "Capacité annuelle" : "Objectif non défini"}
              </span>
            )}
            <span className="text-[11px] text-slate-400">cumul annuel</span>
          </div>
          {etpAttainment.defined && (
            <div className="mt-1 flex flex-wrap items-center justify-between gap-1 text-[10px]">
              <span className="text-slate-300 font-medium">{etpAttainment.attainmentText}</span>
              {etpAttainment.varianceText ? (
                <span className={etpAttainment.relativeVariance >= 0 ? "text-emerald-400 font-medium" : "text-rose-400 font-medium"}>
                  {etpAttainment.varianceText}
                </span>
              ) : null}
            </div>
          )}
        </div>

        {/* CARTE 4: EFFECTIF TOTAL */}
        <div className="rounded-xl border border-white/5 bg-slate-900/80 p-3.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-400">Effectif total (Fin d'année)</span>
            <Users className="h-4 w-4 text-amber-400" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-xl font-bold text-white">
              {actualResources != null ? `${Math.round(actualResources)} pers.` : "—"}
            </span>
            {targetResources != null && (
              <span className="text-xs text-slate-400">
                / {Math.round(targetResources)} pers.
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-xs">
            {resourcesDelta != null ? (
              <span
                className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                  resourcesDelta >= 0
                    ? "bg-emerald-500/10 text-emerald-400"
                    : "bg-rose-500/10 text-rose-400"
                }`}
              >
                {resourcesDelta >= 0 ? "+" : ""}{Math.round(resourcesDelta)} pers.
              </span>
            ) : (
              <span className="text-[11px] text-slate-400">
                {resourcesAttainment.defined ? "Atterrissage" : "Objectif non défini"}
              </span>
            )}
            <span className="text-[11px] text-slate-400">effectif cible</span>
          </div>
          {resourcesAttainment.defined && (
            <div className="mt-1 flex flex-wrap items-center justify-between gap-1 text-[10px]">
              <span className="text-slate-300 font-medium">{resourcesAttainment.attainmentText}</span>
              {resourcesAttainment.varianceText ? (
                <span className={resourcesAttainment.relativeVariance >= 0 ? "text-emerald-400 font-medium" : "text-rose-400 font-medium"}>
                  {resourcesAttainment.varianceText}
                </span>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
